<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mt-4 ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6" class="header-card">
            <v-card-text class="d-flex align-center justify-space-between py-3">
              <div class="d-flex align-center">
                <v-avatar color="primary" size="42" class="mr-3">
                  <v-icon dark>mdi-cash-clock</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">Salary Cycles</div>
                  <div class="grey--text text-caption">Manage pay period configurations</div>
                </div>
              </div>
              <v-btn color="primary" depressed to="/salary-cycles/create">
                <v-icon left>mdi-plus</v-icon>
                New Cycle
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Search & Pagination -->
      <v-row class="ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6">
            <v-card-text class="py-2">
              <v-row align="center">
                <v-col md="4">
                  <v-text-field v-model="search_keyword" label="Search salary cycles..." prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details @keydown.enter="getData" @change="page = 1" clearable></v-text-field>
                </v-col>
                <v-col md="3" v-if="allCompanies.length > 0">
                  <v-autocomplete v-model="filter_company_id" :items="allCompanies" label="Company"
                    prepend-inner-icon="mdi-domain" outlined dense hide-details clearable @input="getData"></v-autocomplete>
                </v-col>
                <v-spacer></v-spacer>
                <v-col md="4" class="d-flex justify-end">
                  <v-pagination v-model="page" :length="paginationLength" circle color="primary" :total-visible="5"></v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mx-6"></v-progress-linear>

      <!-- Cycle Cards Grid -->
      <v-row v-if="!isLoading && salaryCycles.length > 0" class="ml-2 mr-2">
        <v-col md="4" sm="6" cols="12" v-for="(cycle, i) in salaryCycles" :key="`cycle${i}`">
          <v-card elevation="4" class="cycle-card" :class="{ 'inactive-card': !cycle.is_active }">
            <v-card-text class="pb-2">
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip small :color="cycle.is_active ? 'green' : 'grey'" dark>
                  {{ cycle.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
                <div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small :to="`/salary-cycles/${cycle.id}`">
                        <v-icon small color="primary">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit & Assign Users</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" icon small @click="deleteCycle(cycle.id)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </div>

              <div class="text-subtitle-1 font-weight-bold mb-1">{{ cycle.name }}</div>
              <div v-if="allCompanies.length > 0 && cycle.company" class="mb-2">
                <v-chip x-small color="blue-grey lighten-4" text-color="blue-grey darken-3" label>
                  <v-icon x-small left>mdi-domain</v-icon>
                  {{ cycle.company.name }}
                </v-chip>
              </div>

              <div class="d-flex align-center justify-center my-4">
                <div class="text-center day-box primary white--text">
                  <div class="text-h5 font-weight-bold">{{ cycle.start_day }}</div>
                  <div class="text-caption">Start</div>
                </div>
                <v-icon class="mx-3" color="grey">mdi-arrow-right</v-icon>
                <div class="text-center day-box primary white--text">
                  <div class="text-h5 font-weight-bold">{{ cycle.end_day }}</div>
                  <div class="text-caption">End</div>
                </div>
              </div>

              <div class="text-caption grey--text text-center">
                <span v-if="cycle.start_day <= cycle.end_day">
                  {{ ordinal(cycle.start_day) }} to {{ ordinal(cycle.end_day) }} of same month
                </span>
                <span v-else>
                  {{ ordinal(cycle.start_day) }} prev month to {{ ordinal(cycle.end_day) }} current month
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <v-row v-if="!isLoading && salaryCycles.length === 0" class="ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6" class="text-center py-10">
            <v-icon size="64" color="grey lighten-1">mdi-cash-clock</v-icon>
            <div class="text-h6 grey--text mt-3">No salary cycles found</div>
            <div class="grey--text text--lighten-1 mb-4">Create your first salary cycle to get started</div>
            <v-btn color="primary" depressed to="/salary-cycles/create">
              <v-icon left>mdi-plus</v-icon>
              Create Salary Cycle
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      search_keyword: "",
      page: 1,
      paginationLength: 1,
      salaryCycles: [],
      isLoading: true,
      filter_company_id: "all",
      allCompanies: [],
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  watch: {
    page() {
      this.getData();
    },
  },
  methods: {
    ordinal(n) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    async getMasters() {
      try {
        const response = await axios.get("/salary_cycles/masters");
        if (response.data.companies && response.data.companies.length > 0) {
          this.allCompanies = [{ text: "All Companies", value: "all" }];
          response.data.companies.forEach((c) => {
            this.allCompanies.push({ text: c.name, value: c.id });
          });
        }
      } catch (e) {
        console.error("Error fetching masters:", e);
      }
    },
    async getData() {
      this.isLoading = true;
      try {
        const response = await axios.get("/salary_cycles", {
          params: {
            page: this.page,
            rowsPerPage: this.rowsPerPage,
            search_keyword: this.search_keyword,
            filter_company_id: this.filter_company_id || "",
          },
        });
        this.salaryCycles = response.data.data;
        this.paginationLength = Math.ceil(response.data.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching salary cycles:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteCycle(id) {
      if (confirm("Are you sure you want to delete this salary cycle?")) {
        try {
          await axios.post("/salary_cycles/clear", { id: id });

          this.getData();
        } catch (error) {
          console.error("Error deleting salary cycle:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
.header-card {
  border-left: 4px solid #3354f4;
}
.cycle-card {
  border-radius: 12px;
  transition: transform 0.2s, box-shadow 0.2s;
}
.cycle-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}
.inactive-card {
  opacity: 0.7;
}
.day-box {
  width: 70px;
  height: 70px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
